// src/pages/FAQ.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/images';
import { 
  FaGraduationCap, 
  FaBuilding, 
  FaCog, 
  FaRocket, 
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
  FaDiscord,
  FaSearch,
  FaQuestionCircle,
  FaLifeRing
} from 'react-icons/fa';
import MainHeader from './utils/MainHeader'; 

const FAQ = () => {
  const [openSections, setOpenSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const faqData = {
    students: {
      title: "Student FAQ",
      icon: FaGraduationCap,
      color: "blue",
      sections: [
        {
          id: "student-getting-started",
          title: "Getting Started",
          questions: [
            {
              q: "Who can participate in this pilot program?",
              a: "Currently enrolled students at the University of Ghana School of Engineering Sciences. You're strongly encouraged to use your official UG email address to register."
            },
            {
              q: "Is the platform free for students?",
              a: "Yes! The platform is completely free for all students. There are no hidden fees or subscription costs."
            },
            {
              q: "What if I don't have much work experience?",
              a: "No problem! Many internships are designed for students with little to no experience. Focus on your academic projects, skills, and enthusiasm in your profile."
            },
            {
              q: "Can I apply for multiple positions?",
              a: "Absolutely! We encourage applying to multiple relevant positions to increase your chances. There's no limit on applications."
            }
          ]
        },
        {
          id: "student-profile-applications",
          title: "Profile & Applications",
          questions: [
            {
              q: "How do I make my profile stand out?",
              a: "• Complete 100% of your profile\n• Highlight relevant coursework and projects\n• List both technical and soft skills\n• Write a compelling bio that shows your personality\n• Include any certifications or achievements"
            },
            {
              q: "What should I include in my cover letter?",
              a: "• Why you're interested in this specific role\n• Relevant skills and experiences\n• What you hope to learn\n• Why you want to work for this company\n• Keep it concise (2-3 paragraphs)"
            },
            {
              q: "How long does it take to hear back from employers?",
              a: "Most employers respond within 3-5 business days. You can track your application status in real-time on your dashboard."
            },
            {
              q: "What if I get rejected?",
              a: "Don't take it personally! Use it as a learning opportunity. Many employers provide feedback. Keep applying and improving your profile."
            }
          ]
        },
        {
          id: "student-technical",
          title: "Technical Issues",
          questions: [
            {
              q: "I'm not receiving email notifications. What should I do?",
              a: "1. Check your spam/junk folder\n2. Verify your email settings in your profile\n3. Contact support if the issue persists"
            },
            {
              q: "The platform is running slowly. How can I fix this?",
              a: "• Clear your browser cache and cookies\n• Try using a different browser\n• Check your internet connection\n• Update your browser to the latest version"
            },
            {
              q: "Can I use the platform on my phone?",
              a: "Yes! The platform is fully mobile-responsive. You can apply, check notifications, and update your profile from any device."
            }
          ]
        }
      ]
    },
    employers: {
      title: "Employer FAQ",
      icon: FaBuilding,
      color: "orange",
      sections: [
        {
          id: "employer-getting-started",
          title: "Getting Started",
          questions: [
            {
              q: "How do I get approved as an employer?",
              a: "After registering, our admin team will review your application within 1-2 business days. Make sure to use an official company email and provide accurate company information."
            },
            {
              q: "What types of companies can participate?",
              a: "We welcome companies of all sizes and industries, from startups to multinational corporations, as long as you can provide meaningful internship experiences."
            },
            {
              q: "Is there a cost to use the platform?",
              a: "The pilot program is free for all participating employers. Future pricing will be discussed after the pilot phase."
            }
          ]
        },
        {
          id: "employer-posting",
          title: "Posting Positions",
          questions: [
            {
              q: "How many positions can I post?",
              a: "There's no limit on the number of positions you can post during the pilot program."
            },
            {
              q: "Can I edit a position after posting it?",
              a: "Yes! You can edit position details anytime. However, if you make significant changes, consider notifying applicants who have already applied."
            },
            {
              q: "Should I include salary information?",
              a: "It's recommended but not required. Students appreciate transparency about compensation, even if it's unpaid but provides other benefits like training or certificates."
            },
            {
              q: "How do I attract quality candidates?",
              a: "• Write detailed, engaging job descriptions\n• Highlight learning opportunities and mentorship\n• Showcase your company culture\n• Respond quickly to applications\n• Provide feedback to candidates"
            }
          ]
        },
        {
          id: "employer-applications",
          title: "Managing Applications",
          questions: [
            {
              q: "How do I review applications efficiently?",
              a: "Use the filtering and sorting features in the Application Management section. You can filter by skills, experience level, and application date."
            },
            {
              q: "Should I provide feedback to rejected candidates?",
              a: "Yes, when possible. Even brief feedback helps students improve and creates a positive impression of your company."
            },
            {
              q: "Can I schedule interviews through the platform?",
              a: "Currently, you'll need to coordinate interviews directly with candidates via email or phone. Interview scheduling features may be added in future updates."
            }
          ]
        }
      ]
    },
    technical: {
      title: "Technical FAQ",
      icon: FaCog,
      color: "green",
      sections: [
        {
          id: "tech-account-security",
          title: "Account & Security",
          questions: [
            {
              q: "How do I reset my password?",
              a: "Click 'Forgot Password' on the login page and follow the instructions sent to your email."
            },
            {
              q: "Is my personal data secure?",
              a: "Yes! We use Firebase security and follow industry best practices to protect your data. Your information is only visible to relevant users (employers can't see other employers' data, etc.)."
            },
            {
              q: "Can I delete my account?",
              a: "Yes, contact our support team to request account deletion. Note that this action cannot be undone."
            }
          ]
        },
        {
          id: "tech-browser-compatibility",
          title: "Browser & Compatibility",
          questions: [
            {
              q: "Which browsers work best with the platform?",
              a: "We recommend Chrome, Firefox, Safari, or Edge (latest versions). The platform works on both desktop and mobile browsers."
            },
            {
              q: "I'm getting a 'Firebase Configuration Error.' What does this mean?",
              a: "This is usually a temporary issue. Try refreshing the page or clearing your browser cache. If it persists, contact support."
            },
            {
              q: "The platform won't load properly. What should I try?",
              a: "1. Refresh the page\n2. Clear browser cache and cookies\n3. Disable browser extensions temporarily\n4. Try using incognito/private browsing mode\n5. Switch to a different browser"
            }
          ]
        },
        {
          id: "tech-features",
          title: "Features",
          questions: [
            {
              q: "How does the 'Smart Matching' feature work?",
              a: "The platform uses your profile information (skills, interests, location preferences) to suggest relevant opportunities. The more complete your profile, the better the matches."
            },
            {
              q: "How often should I update my profile?",
              a: "Update your profile whenever you learn new skills, complete projects, or gain experience. Regular updates keep you visible to employers."
            },
            {
              q: "Can I have multiple versions of my profile?",
              a: "Currently, you can have one profile, but you can customize your applications for different positions by tailoring your cover letter and responses."
            }
          ]
        }
      ]
    },
    pilot: {
      title: "Pilot Program FAQ",
      icon: FaRocket,
      color: "purple",
      sections: [
        {
          id: "pilot-details",
          title: "Program Details",
          questions: [
            {
              q: "How long will the pilot program last?",
              a: "The pilot program is planned for approximately 10 weeks, divided into setup, active usage, and evaluation phases."
            },
            {
              q: "What happens after the pilot program ends?",
              a: "Based on feedback and success metrics, we'll determine the next steps for broader platform launch. Successful participants may get early access to the full platform."
            },
            {
              q: "Am I required to provide feedback?",
              a: "While not mandatory, your feedback is crucial for improving the platform. We encourage active participation in surveys and feedback sessions."
            }
          ]
        },
        {
          id: "pilot-support",
          title: "Support & Assistance",
          questions: [
            {
              q: "What kind of support is available during the pilot?",
              a: "• Email support (info.oodlestech@gmail.com)\n• Discord community for peer support\n• Contact Mr. Joseph Tetteh Portuphy (jtportuphy@ug.edu.gh)"
            },
            {
              q: "Can I suggest new features?",
              a: "Absolutely! We welcome all suggestions. You can do that in our Discord community or contact support directly."
            }
          ]
        }
      ]
    },
    troubleshooting: {
      title: "Troubleshooting",
      icon: FaExclamationTriangle,
      color: "red",
      sections: [
        {
          id: "troubleshooting-common",
          title: "Common Issues & Solutions",
          questions: [
            {
              q: "Permission denied error",
              a: "• Log out and log back in\n• Clear browser cache\n• Check if your account is properly verified\n• Contact support if persists"
            },
            {
              q: "Profile changes not saving",
              a: "• Make sure all required fields are filled\n• Refresh the page after saving\n• Try using a different browser\n• Disable ad blockers temporarily"
            },
            {
              q: "Applications not submitting",
              a: "• Check your internet connection\n• Ensure all required fields are completed\n• Try submitting again after a few minutes\n• Contact support if error continues"
            },
            {
              q: "Not receiving email notifications",
              a: "• Check spam/junk folders\n• Verify email address in profile settings\n• Add platform domain to your email whitelist\n• Contact your IT department if using university email"
            }
          ]
        }
      ]
    }
  };

  const getAllQuestions = () => {
    const allQuestions = [];
    Object.values(faqData).forEach(category => {
      category.sections.forEach(section => {
        section.questions.forEach(question => {
          allQuestions.push({
            ...question,
            category: category.title,
            section: section.title,
            categoryKey: Object.keys(faqData).find(key => faqData[key] === category)
          });
        });
      });
    });
    return allQuestions;
  };

  const filteredQuestions = () => {
    const allQuestions = getAllQuestions();
    
    return allQuestions.filter(item => {
      const matchesSearch = searchTerm === '' || 
        item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.a.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || item.categoryKey === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  };

  const categories = [
    { key: 'all', label: 'All Categories', icon: FaQuestionCircle },
    { key: 'students', label: 'Students', icon: FaGraduationCap },
    { key: 'employers', label: 'Employers', icon: FaBuilding },
    { key: 'technical', label: 'Technical', icon: FaCog },
    { key: 'pilot', label: 'Pilot Program', icon: FaRocket },
    { key: 'troubleshooting', label: 'Troubleshooting', icon: FaExclamationTriangle }
  ];

  const FAQItem = ({ question, answer, questionId }) => {
    const isOpen = openSections[questionId];
    
    return (
      <div className="border border-gray-200 rounded-lg mb-4">
        <button
          onClick={() => toggleSection(questionId)}
          className="w-full text-left p-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
        >
          <span className="font-medium text-gray-800 pr-4">{question}</span>
          {isOpen ? 
            <FaChevronUp className="text-gray-500 flex-shrink-0" /> : 
            <FaChevronDown className="text-gray-500 flex-shrink-0" />
          }
        </button>
        {isOpen && (
          <div className="px-4 pb-4">
            <div className="pt-2 border-t border-gray-100">
              <p className="text-gray-600 whitespace-pre-line">{answer}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <MainHeader />

      {/* Hero Section */}
      <section className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <h1 className="text-4xl md:text-5xl font-bold">
              Frequently Asked Questions
            </h1>
          </div>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto">
            University of Ghana School of Engineering Sciences Pilot Program
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.key}
                    onClick={() => setSelectedCategory(category.key)}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedCategory === category.key
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <IconComponent className="mr-2 text-sm" />
                    {category.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {searchTerm || selectedCategory !== 'all' ? (
            // Search Results
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Search Results ({filteredQuestions().length} found)
              </h2>
              <div className="space-y-4">
                {filteredQuestions().map((item, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border">
                    <div className="p-4">
                      <div className="text-sm text-blue-600 mb-2">
                        {item.category} → {item.section}
                      </div>
                      <FAQItem
                        question={item.q}
                        answer={item.a}
                        questionId={`search-${index}`}
                      />
                    </div>
                  </div>
                ))}
                {filteredQuestions().length === 0 && (
                  <div className="text-center py-12">
                    <FaSearch className="text-gray-400 text-4xl mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No results found</h3>
                    <p className="text-gray-500">Try adjusting your search terms or category filter.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Category View
            <div className="space-y-12">
              {Object.entries(faqData).map(([categoryKey, category]) => {
                const IconComponent = category.icon;
                const colorClasses = {
                  blue: 'bg-blue-100 text-blue-600',
                  orange: 'bg-orange-100 text-orange-600',
                  green: 'bg-green-100 text-green-600',
                  purple: 'bg-purple-100 text-purple-600',
                  red: 'bg-red-100 text-red-600'
                };

                return (
                  <div key={categoryKey} className="bg-white rounded-xl shadow-sm border p-8">
                    <div className="flex items-center mb-8">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${colorClasses[category.color]}`}>
                        <IconComponent className="text-2xl" />
                      </div>
                      <h2 className="text-3xl font-bold text-gray-800">{category.title}</h2>
                    </div>

                    <div className="space-y-8">
                      {category.sections.map((section) => (
                        <div key={section.id}>
                          <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                            {section.title}
                          </h3>
                          <div className="space-y-4">
                            {section.questions.map((item, index) => (
                              <FAQItem
                                key={`${section.id}-${index}`}
                                question={item.q}
                                answer={item.a}
                                questionId={`${section.id}-${index}`}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-16 bg-gradient-to-r from-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <FaLifeRing className="text-3xl mr-3" />
            <h2 className="text-3xl font-bold">Still Need Help?</h2>
          </div>
          
          <p className="text-xl mb-8 opacity-90">
            Can't find what you're looking for? Our support team is here to help!
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <FaEnvelope className="text-2xl mx-auto mb-4 text-blue-400" />
              <h3 className="text-lg font-semibold mb-2">Email Support</h3>
              <a 
                href="mailto:info.oodlestech@gmail.com" 
                className="text-blue-400 hover:text-blue-300"
              >
                info.oodlestech@gmail.com
              </a>
            </div>

            <div className="text-center">
              <FaDiscord className="text-2xl mx-auto mb-4 text-purple-400" />
              <h3 className="text-lg font-semibold mb-2">Discord Community</h3>
              <a 
                href="https://discord.gg/2K6zpbJb5y" 
                className="text-purple-400 hover:text-purple-300">
                Join Community
              </a>
            </div>

            <div className="text-center">
              <FaGraduationCap className="text-2xl mx-auto mb-4 text-green-400" />
              <h3 className="text-lg font-semibold mb-2">University Coordinator</h3>
              <a 
                href="mailto:jtportuphy@ug.edu.gh" 
                className="text-green-400 hover:text-green-300"
              >
                Joseph Tetteh Portuphy
              </a>
            </div>
          </div>

          <div className="mt-12 p-6 bg-yellow-900 bg-opacity-50 rounded-lg max-w-2xl mx-auto">
            <p className="text-yellow-200">
              <strong>Remember:</strong> This is a pilot program, and your experience and feedback are invaluable 
              in helping us create the best possible platform for future users!
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <img src={Logo} className="w-6 h-6 mr-2" alt="Logo"/>
            <span className="text-lg font-semibold">Oodles Internships</span>
          </div>
          <p className="text-gray-400 mb-2">
            FAQ Document v1.0 - University of Ghana School of Engineering Sciences Pilot Program
          </p>
          <p className="text-sm text-gray-500">
            Your feedback helps us improve the platform for everyone
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;